import { Typography } from "@mui/material";
import React, { useCallback } from "react";

import { RichTooltip } from "~/components/UI/RichTooltip";
import type { IOption } from "~/types/option";

import * as Styled from "./FilterTabs.styles";

export interface FilterTabsProps<TValue extends React.Key = string> {
  options: IOption<TValue>[];
  value: TValue | null;
  onChange: (value: TValue) => void;
  fullWidth?: boolean;
}

export const FilterTabs = <TValue extends React.Key = string>({
  options,
  value,
  onChange,
  fullWidth = false,
}: FilterTabsProps<TValue>) => {
  const handleChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newValue: TValue | null) => {
      if (newValue !== null) {
        onChange(newValue);
      }
    },
    [onChange]
  );

  return (
    <Styled.Group
      value={value}
      exclusive
      onChange={handleChange}
      size="small"
      fullWidth={fullWidth}
    >
      {options.map((option) => {
        const IconComponent = option.icon;
        const tab = (
          <Styled.Tab value={option.value} disabled={option.disabled}>
            {IconComponent && <IconComponent />}
            <Typography
              variant="caption"
              sx={{
                fontSize: 13,
                fontWeight: 500
              }}>
              {option.label}
            </Typography>
          </Styled.Tab>
        );

        const tabContainer = (
          <Styled.TabContainer key={option.value} $fullWidth={fullWidth}>
            {tab}
          </Styled.TabContainer>
        );

        if (!option.tooltip) return tabContainer;

        return (
          <RichTooltip key={option.value} title={option.tooltip} maxWidth={280}>
            {tabContainer}
          </RichTooltip>
        );
      })}
    </Styled.Group>
  );
};
